import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Users, Plus, Trash2, Edit3, Shield, Eye, CheckCircle, XCircle, Loader2, AlertCircle } from 'lucide-react';
import { supabase } from '../../config/supabase';

const ProfileUserManagement = ({ profileId, currentUserEmail, onClose, isCmsAdmin = false }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserRole, setNewUserRole] = useState('viewer');
  const [newUserCanEdit, setNewUserCanEdit] = useState(false);
  const [newUserCanApprove, setNewUserCanApprove] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);

  useEffect(() => {
    fetchUsers();
  }, [profileId]);

  const fetchUsers = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: err } = await supabase.rpc('get_profile_users', {
        p_profile_id: profileId
      });
      if (err) throw err;
      setUsers(data || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    if (!newUserEmail.trim()) return;

    setActionLoading('add');
    try {
      const { data, error: err } = await supabase.rpc('assign_user_to_profile', {
        p_profile_id: profileId,
        p_user_email: newUserEmail.toLowerCase().trim(),
        p_assigned_by: currentUserEmail,
        p_role: newUserRole,
        p_can_edit_profile: newUserCanEdit,
        p_can_approve_signals: newUserCanApprove
      });

      if (err) throw err;
      if (data && !data.success) throw new Error(data.error);

      // Reset form
      setNewUserEmail('');
      setNewUserRole('viewer');
      setNewUserCanEdit(false);
      setNewUserCanApprove(false);
      setShowAddForm(false);

      // Refresh list
      await fetchUsers();
    } catch (err) {
      alert(`Failed to add user: ${err.message}`);
    } finally {
      setActionLoading(null);
    }
  };

  const handleRemoveUser = async (userEmail) => {
    if (!window.confirm(`Remove ${userEmail} from this profile?`)) return;

    setActionLoading(`remove-${userEmail}`);
    try {
      const { data, error: err } = await supabase.rpc('remove_user_from_profile', {
        p_profile_id: profileId,
        p_user_email: userEmail,
        p_removed_by: currentUserEmail
      });

      if (err) throw err;
      if (data && !data.success) throw new Error(data.error);

      await fetchUsers();
    } catch (err) {
      alert(`Failed to remove user: ${err.message}`);
    } finally {
      setActionLoading(null);
    }
  };

  const handleChangeRole = async (userEmail, newRole) => {
    setActionLoading(`role-${userEmail}`);
    try {
      const { data, error: err } = await supabase.rpc('change_profile_user_role', {
        p_profile_id: profileId,
        p_user_email: userEmail,
        p_new_role: newRole,
        p_changed_by: currentUserEmail
      });

      if (err) throw err;
      if (data && !data.success) throw new Error(data.error);

      await fetchUsers();
    } catch (err) {
      alert(`Failed to change role: ${err.message}`);
    } finally {
      setActionLoading(null);
    }
  };

  const getRoleBadge = (role) => {
    const styles = {
      owner: 'bg-purple-100 text-purple-700',
      admin: 'bg-blue-100 text-blue-700',
      editor: 'bg-green-100 text-green-700',
      viewer: 'bg-gray-100 text-gray-700'
    };
    const icons = {
      owner: Shield,
      admin: CheckCircle,
      editor: Edit3,
      viewer: Eye
    };
    const Icon = icons[role];
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${styles[role]}`}>
        <Icon className="w-3 h-3" />
        {role.charAt(0).toUpperCase() + role.slice(1)}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <Users className="w-5 h-5 text-gray-700" />
          <h3 className="text-lg font-semibold text-gray-900">Profile Users</h3>
          <span className="text-sm text-gray-500">({users.length})</span>
        </div>
        <button
          onClick={() => setShowAddForm(!showAddForm)}
          className="px-3 py-1.5 bg-primary-600 hover:bg-primary-700 text-white rounded-lg text-sm font-medium flex items-center gap-1.5 transition-colors"
        >
          <Plus className="w-4 h-4" />
          Add User
        </button>
      </div>

      {error && (
        <div className="flex items-center gap-2 p-3 mb-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          {error}
        </div>
      )}

      {/* Add user form */}
      {showAddForm && (
        <motion.form
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          onSubmit={handleAddUser}
          className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200"
        >
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email Address *
              </label>
              <input
                type="email"
                value={newUserEmail}
                onChange={(e) => setNewUserEmail(e.target.value)}
                placeholder="user@example.com"
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Role *
              </label>
              <select
                value={newUserRole}
                onChange={(e) => {
                  setNewUserRole(e.target.value);
                  // Auto-set permissions based on role
                  if (e.target.value === 'owner' || e.target.value === 'admin') {
                    setNewUserCanEdit(true);
                    setNewUserCanApprove(true);
                  } else if (e.target.value === 'editor') {
                    setNewUserCanEdit(true);
                    setNewUserCanApprove(false);
                  } else {
                    setNewUserCanEdit(false);
                    setNewUserCanApprove(false);
                  }
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="viewer">Viewer (Read-only)</option>
                <option value="editor">Editor (Can edit profile)</option>
                <option value="admin">Admin (Can approve signals)</option>
                <option value="owner">Owner (Full control)</option>
              </select>
            </div>
          </div>

          <div className="flex items-center gap-4 mt-3">
            <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
              <input
                type="checkbox"
                checked={newUserCanEdit}
                onChange={(e) => setNewUserCanEdit(e.target.checked)}
                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              Can Edit Profile
            </label>

            <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
              <input
                type="checkbox"
                checked={newUserCanApprove}
                onChange={(e) => setNewUserCanApprove(e.target.checked)}
                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              Can Approve Signals
            </label>
          </div>

          <div className="flex items-center gap-2 mt-4">
            <button
              type="submit"
              disabled={actionLoading === 'add'}
              className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50 flex items-center gap-1.5"
            >
              {actionLoading === 'add' ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Adding...
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4" />
                  Add User
                </>
              )}
            </button>
            <button
              type="button"
              onClick={() => setShowAddForm(false)}
              className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg text-sm font-medium transition-colors"
            >
              Cancel
            </button>
          </div>
        </motion.form>
      )}

      {/* User list */}
      <div className="space-y-2">
        {users.map((user) => (
          <motion.div
            key={user.id}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200 hover:border-gray-300 transition-colors"
          >
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-1">
                <span className="text-sm font-medium text-gray-900">{user.user_email}</span>
                {getRoleBadge(user.role)}
              </div>
              <div className="flex items-center gap-3 text-xs text-gray-500">
                {user.can_edit_profile && (
                  <span className="flex items-center gap-1">
                    <CheckCircle className="w-3 h-3 text-green-600" />
                    Can edit profile
                  </span>
                )}
                {user.can_approve_signals && (
                  <span className="flex items-center gap-1">
                    <CheckCircle className="w-3 h-3 text-blue-600" />
                    Can approve signals
                  </span>
                )}
                <span>Added: {new Date(user.assigned_at).toLocaleDateString()}</span>
              </div>
            </div>

            <div className="flex items-center gap-1.5 flex-shrink-0">
              {/* Inline role change — CMS admins can promote/demote */}
              {isCmsAdmin && (
                <select
                  value={user.role}
                  onChange={(e) => handleChangeRole(user.user_email, e.target.value)}
                  disabled={actionLoading === `role-${user.user_email}`}
                  className="text-xs border border-gray-300 rounded-lg px-2 py-1.5 bg-white focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:opacity-50"
                >
                  <option value="viewer">Viewer</option>
                  <option value="editor">Editor</option>
                  <option value="admin">Admin</option>
                  <option value="owner">Owner</option>
                </select>
              )}

              {/* CMS admins/developer can remove anyone; others cannot remove owners */}
              {(user.role !== 'owner' || isCmsAdmin) && (
                <button
                  onClick={() => handleRemoveUser(user.user_email)}
                  disabled={actionLoading === `remove-${user.user_email}`}
                  className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                  title={user.role === 'owner' ? 'Remove owner (admin)' : 'Remove user'}
                >
                  {actionLoading === `remove-${user.user_email}` ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Trash2 className="w-4 h-4" />
                  )}
                </button>
              )}
            </div>
          </motion.div>
        ))}

        {users.length === 0 && (
          <div className="text-center py-8 text-gray-500 text-sm">
            No users assigned to this profile yet.
          </div>
        )}
      </div>
    </div>
  );
};

export default ProfileUserManagement;
